import { cardChanges, fieldOf, sameValues } from "./draft-changes";
import type { EditSource } from "./draft-store";
import { withoutHandMarks } from "./marks";
import type { PostDraft, PostShift } from "./model";

// hand_set keys are the snapshot's field names (docs/page-snapshot.md); the server refuses a chat fill to one the manager did not name.
const HAND_FIELDS = new Set([
  "day",
  "time",
  "where",
  "parking",
  "arrival",
  "manager",
  "attachments",
  "position",
  "pay",
  "count",
  "tips",
  "audience",
  "uniform",
]);

/** The snapshot fields of a card an edit changed. */
export const changedFields = (before: PostShift, after: PostShift): Array<string> => [
  // Keys only: the audience's words (engageOn) never decide which fields were set by hand.
  ...new Set(cardChanges(before, after, true).flatMap(({ key }) => (HAND_FIELDS.has(fieldOf(key)) ? [key] : []))),
];

/** A role's key names it, after a colon; a key of a role no longer on the card is dropped. */
export const stillThere = (key: string, shift: PostShift) => {
  const at = key.indexOf(":");
  return at === -1 || shift.positions.some((position) => position.key === key.slice(at + 1));
};

/**
 * The draft after an edit with each card's `hand_set` brought up to date: a hand edit adds the fields it changed, an edit by
 * the chat (with the manager's go-ahead) takes them off, so the field is the chat's again.
 */
export const withHandSet = (before: PostDraft, after: PostDraft, source: EditSource): PostDraft => {
  const owned = withOwnedFields(before, after, source);
  // A hand edit makes the row the manager's again: the chat's mark on it goes.
  return source === "hand" ? withoutHandMarks(before, owned) : owned;
};

const withOwnedFields = (before: PostDraft, after: PostDraft, source: EditSource): PostDraft => {
  let changed = false;
  const shifts = after.shifts.map((shift) => {
    const was = before.shifts.find(({ key }) => key === shift.key);
    if (!was) return shift;
    const fields = changedFields(was, shift);
    const current = (shift.hand_set ?? []).filter((key) => stillThere(key, shift));
    const next =
      source === "hand" ? [...new Set([...current, ...fields])] : current.filter((key) => !fields.includes(key));
    if (sameValues(next, shift.hand_set)) return shift;
    changed = true;
    return { ...shift, hand_set: next };
  });
  return changed ? { ...after, shifts } : after;
};
